import { Component } from 'react';
import { InputFileInfo } from '../../../../types/file-upload.types';
import { isVideoFromType, isAudioFromType, isImageFromType, isVideoToType, isAudioToType, isImageToType } from '../../../../utils/media';
import { toReadableBits, toReadableBytes } from '../../../../utils/file';
import { DateTimeUtils } from '../../../../ui-utils/date-time.utils';
import styles from './file-info-box.component.module.scss'
import { ConvertOptionsContext } from '../../../convert-options/convert-options-context';
import { untilDestroyed } from '../../../../utils/until-destroyed';
import { cloneDeep, includes, toInteger } from '../../../../utils/lodash-min';
import { Logger } from '../../../../models/logger';
import { ConvertOptionsContextObserver, ConvertOptionsProvider, ConvertOptionsSummary } from '../../../convert-options-v2/convert-options.context';
export interface FileInfoBoxProps {
    fileInfo: InputFileInfo;
    convertOptionsContext: ConvertOptionsContextObserver;
    convertOptionsProvider: ConvertOptionsProvider;
    fromType: string[];
    toType: string;
}

export interface FileInfoBoxState {
    outputFileSize: number;
}

export class FileInfoBoxComponent extends Component<FileInfoBoxProps> {
    private readonly logger = Logger.getLogger('FileInfoBoxComponent');

    private lastEmittedOptionsSummary: ConvertOptionsSummary;

    readonly state: Readonly<FileInfoBoxState> = {
        outputFileSize: -1,
    };
    constructor(props) {
        super(props);
    }

    isConvertFromAndToVideo() {
        return isVideoFromType(this.props.fromType) && isVideoToType(this.props.toType);
    }

    isConvertFromAndToAudio() {
        return isAudioFromType(this.props.fromType) && isAudioToType(this.props.toType);
    }

    isConvertFromAndToImage() {
        return isImageFromType(this.props.fromType) && isImageToType(this.props.toType);
    }

    componentDidMount(): void {
        if (this.isConvertFromAndToVideo() || this.isConvertFromAndToImage() || this.isConvertFromAndToAudio()) {
            this.props.convertOptionsContext.modelChangeObserver
                .pipe(untilDestroyed(this))
                .subscribe({
                    next: (summary) => {
                        this.lastEmittedOptionsSummary = summary
                        this.updateEstimatedOutputFileSize(summary);
                    }
                });
        }
    }

    updateEstimatedOutputFileSize(summary: ConvertOptionsSummary) {
        try {
            this.setState({
                outputFileSize: -1,
            });
            if (this.isConvertFromAndToImage() || this.isConvertFromAndToAudio() || this.isConvertFromAndToVideo()) {
                const sizePercentage = summary.sizePercentage;
                const fileSize = summary.fileSize;
                if (sizePercentage) {
                    const percentage = parseInt(sizePercentage);
                    this.setState({
                        outputFileSize: this.props.fileInfo.size * (percentage / 100)
                    });
                } else if (fileSize) {
                    const targetSizeOption = fileSize.split("::");
                    const selectedSizeValue = targetSizeOption[0];
                    const selectedSizeUnit = targetSizeOption[1];
                    let targetSize = parseFloat(selectedSizeValue);
                    if (selectedSizeUnit === "MEGABYTES" || selectedSizeUnit === "MB") {
                        targetSize = targetSize * 1000 * 1000;
                    }
                    if (selectedSizeUnit === "KILOBYTES" || selectedSizeUnit === "kB" || selectedSizeUnit === "KB") {
                        targetSize = targetSize * 1000;
                    }
                    this.setState({
                        outputFileSize: toInteger(targetSize),
                    });
                }
            }
        } catch (e) {
            this.logger.error({
                message: "Exception thrown while calculating estimated file size",
                data: this.props.fileInfo,
                error: e,
            });
        }
    }

    createTag(key: string, value: any, isPrimary = false) {
        return (
            <div className={`${styles['info-tag']} ${isPrimary ? styles['info-tag-primary'] : ''}`}>
                <span className={styles['label']}>{key}:</span>
                <span className={styles['value']}>{value}</span>
            </div>
        );
    }

    render() {
        const shouldDisplayWidth = this.props.fileInfo.width && this.props.fileInfo.width > -1;
        const shouldDisplayHeight = this.props.fileInfo.height && this.props.fileInfo.height > -1;
        const shouldDisplayWidthAndHeight = shouldDisplayWidth && shouldDisplayHeight;

        return (
            <div className={styles['file-info']}>
                {this.props.fileInfo.duration && this.props.fileInfo.duration > -1 &&
                    this.createTag('Duration', DateTimeUtils.secondsToTime(this.props.fileInfo.duration, true))}

                {shouldDisplayWidthAndHeight &&
                    this.createTag('WxH', `${this.props.fileInfo.width}x${this.props.fileInfo.height}px`)}

                {this.props.fileInfo.video_bitrate && this.props.fileInfo.video_bitrate > -1 &&
                    this.createTag('Video Bitrate', toReadableBits(this.props.fileInfo.video_bitrate, 'rate'))}

                {this.props.fileInfo.audio_bitrate && this.props.fileInfo.audio_bitrate > -1 &&
                    this.createTag('Audio Bitrate', toReadableBits(this.props.fileInfo.audio_bitrate, 'rate'))}

                {this.props.fileInfo.framerate && this.props.fileInfo.framerate > 0 &&
                    this.createTag('FPS', this.props.fileInfo.framerate.toFixed(2))}

                {this.props.fileInfo.numberOfFrames && this.props.fileInfo.numberOfFrames > 0 &&
                    this.createTag('Frames', this.props.fileInfo.numberOfFrames.toLocaleString())}

                {this.props.fileInfo.numberOfPages && this.props.fileInfo.numberOfPages > -1 &&
                    this.createTag('Pages', this.props.fileInfo.numberOfPages)}

                {this.props.fileInfo.size && this.props.fileInfo.size > -1 &&
                    this.createTag('Size', toReadableBytes(this.props.fileInfo.size, 'quantity'))}

                {this.state.outputFileSize && this.state.outputFileSize > -1 &&
                    this.createTag('Est. Size', toReadableBytes(this.state.outputFileSize, 'quantity'), true)}
            </div>
        );
    }
}
